import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, Input, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import {
    CULTURE_COOKIE, ENGLISH, cookieValue, languageInfo, suggestedLanguage, SUGGEST_DISMISSED_COOKIE, SUGGESTIONS,
} from './culture.config';
import { CultureService } from './culture.service';

/** One language in the switch: its name in itself, and this page's address in it. */
export interface CultureSwitchTarget {
    code: string;
    nativeName: string;
    href: string;
    current: boolean;
}

/** Remembers the visitor's pick (5.6): every address without a prefix is shown in it from the next page load on, and emails and SMS go out in it. */
export function rememberLanguage(document: Document, code: string): void {
    try {
        document.cookie = CULTURE_COOKIE + '=' + encodeURIComponent(code) + '; path=/; max-age=31536000; SameSite=Lax';
    } catch { /* a browser that refuses cookies gets English mail */ }
}

/** The host's languages as links to this page (5.6), for a header's switch: a click sets the language, the link loads the page again. */
export function switchTargets(culture: CultureService, url: string): CultureSwitchTarget[] {
    return culture.languages.map(code => ({
        code,
        nativeName: languageInfo(code).nativeName,
        href: culture.urlIn(code, url),
        current: code === culture.lang,
    }));
}

/**
 * The language switch (Multilang_Culture_Spec.md 5.6): every language by its own name, each a plain link to this page that
 * sets the visitor's language first - a full page load, never an in-place swap. A header that has its own dropdown uses switchTargets().
 */
@Component({
    selector: 'app-culture-switch',
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        <ul class="culture-switch" [attr.aria-label]="label || ('language' | t:'Language')">
            <li *ngFor="let target of targets" class="culture-switch__item">
                <a class="culture-switch__link" [class.culture-switch__link--current]="target.current"
                   [attr.href]="target.href" [attr.hreflang]="target.code" [attr.lang]="target.code"
                   [attr.aria-current]="target.current ? 'true' : null" (click)="choose(target.code)">{{ target.nativeName }}</a>
            </li>
        </ul>`,
    styles: [`
        .culture-switch { display: flex; flex-wrap: wrap; gap: 4px 12px; list-style: none; margin: 0; padding: 0; }
        .culture-switch__link { color: inherit; }
        .culture-switch__link--current { font-weight: 500; text-decoration: none; pointer-events: none; }
    `],
})
export class CultureSwitchComponent implements OnInit, OnDestroy {
    /** The list's accessible name; the "language" label unless given. */
    @Input() label: string;
    targets: CultureSwitchTarget[] = [];
    private sub: Subscription;

    constructor(private culture: CultureService, private router: Router, private cd: ChangeDetectorRef,
                @Inject(DOCUMENT) private document: any) { }

    ngOnInit(): void {
        this.targets = switchTargets(this.culture, this.router.url);
        this.sub = this.router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe(() => {
            this.targets = switchTargets(this.culture, this.router.url);
            this.cd.markForCheck();
        });
    }

    ngOnDestroy(): void {
        if (this.sub) { this.sub.unsubscribe(); }
    }

    choose(code: string): void {
        rememberLanguage(this.document, code);
    }
}

/**
 * The suggestion (5.6): a small dismissible note, in the language it offers, when the visitor's browser language - or,
 * failing that, their country's - is another of the site's languages than the page's: "এই পেজটি বাংলায় দেখুন?". Never a
 * redirect. Browser only, never in the server's HTML; turned down once, never offered again for that language, and never
 * once the visitor has picked a language themselves (the switch's culture cookie): that choice is theirs and it stays.
 */
@Component({
    selector: 'app-culture-suggest',
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        <div *ngIf="offer" class="culture-suggest" role="dialog" [attr.lang]="offer" [attr.dir]="offerDir" aria-live="polite">
            <span class="culture-suggest__ask">{{ words.ask }}</span>
            <a class="culture-suggest__yes" [attr.href]="href" [attr.hreflang]="offer" (click)="accept()">{{ words.yes }}</a>
            <button type="button" class="culture-suggest__no" (click)="dismiss()">{{ words.no }}</button>
        </div>`,
    styles: [`
        .culture-suggest {
            /* Under the theme's drawers (the phone menu 1000, the side cart 2000) and the cookie notice (999). */
            position: fixed; z-index: 998; inset-block-end: 16px; inset-inline-start: 16px; max-width: calc(100vw - 32px);
            display: flex; flex-wrap: wrap; align-items: center; gap: 8px 12px; padding: 10px 14px;
            background: #fff; color: #3d464d; border-radius: 6px; box-shadow: 0 2px 14px rgba(0, 0, 0, .18); font-size: 14px;
        }
        .culture-suggest__yes {
            padding: 4px 12px; border-radius: 4px; background: #3d464d; color: #fff; text-decoration: none; font-weight: 500;
        }
        .culture-suggest__no { padding: 4px 6px; border: 0; background: none; color: #6c757d; cursor: pointer; font-size: 13px; }
    `],
})
export class CultureSuggestComponent implements OnInit, OnDestroy {
    /** The visitor's country (two letters), when the host knows it. */
    @Input() set country(value: string) {
        this.visitorCountry = value || '';
        this.decide();
    }

    offer: string = null;
    offerDir = 'ltr';
    href = '';
    words = SUGGESTIONS[ENGLISH];
    private visitorCountry = '';
    private sub: Subscription;

    constructor(private culture: CultureService, private router: Router, private cd: ChangeDetectorRef,
                @Inject(DOCUMENT) private document: any, @Inject(PLATFORM_ID) private platformId: any) { }

    ngOnInit(): void {
        this.decide();
        this.sub = this.router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe(() => this.decide());
    }

    ngOnDestroy(): void {
        if (this.sub) { this.sub.unsubscribe(); }
    }

    accept(): void {
        rememberLanguage(this.document, this.offer);
    }

    dismiss(): void {
        const turnedDown = this.dismissed().concat([this.offer]).filter((v, i, all) => v && all.indexOf(v) === i);
        try {
            this.document.cookie = SUGGEST_DISMISSED_COOKIE + '=' + encodeURIComponent(turnedDown.join(',')) + '; path=/; max-age=315360000; SameSite=Lax';
        } catch { /* asked again next time */ }
        this.offer = null;
        this.cd.markForCheck();
    }

    private dismissed(): string[] {
        return cookieValue(this.document.cookie, SUGGEST_DISMISSED_COOKIE).split(',').map(s => s.trim()).filter(s => s);
    }

    private decide(): void {
        if (!isPlatformBrowser(this.platformId) || this.culture.languages.length < 2) { return; }
        // Not over a form someone is filling in (the account area and its checkout), nor to someone who chose a language.
        if (this.culture.bare(this.router.url).indexOf('/account') === 0 || cookieValue(this.document.cookie, CULTURE_COOKIE)) {
            this.offer = null;
            this.cd.markForCheck();
            return;
        }
        const browser: string[] = (navigator.languages && navigator.languages.length ? Array.from(navigator.languages) : [navigator.language]);
        const offer = suggestedLanguage(this.culture.lang, this.culture.languages, browser, this.visitorCountry, this.dismissed());
        this.offer = offer;
        if (offer) {
            this.words = SUGGESTIONS[offer] || { ask: languageInfo(offer).nativeName + '?', yes: languageInfo(offer).nativeName, no: '×' };
            this.offerDir = languageInfo(offer).direction;
            this.href = this.culture.urlIn(offer, this.router.url);
        }
        this.cd.markForCheck();
    }
}
